import {
  Card,
  CardBody,
  CardHeader,
  Alert,
  SelectListSingle,
  RadioGroup,
  CardTitle
} from '@/common'
import { useTranslation } from 'react-i18next'
import { HeatFlowSystems } from '@/features/production-routes/api/ProductionRouteModel'
import { useCallback, useState } from 'react'
import { StepFooter } from '@/shared/components'
function PurificationOptionForm({
  onChange,
  value,
  label,
  name
}: {
  onChange: (value: boolean) => void
  value: boolean
  name: string
  label: string
}) {
  const { t } = useTranslation()

  return (
    <div>
      <RadioGroup
        label={label}
        value={value}
        name={name}
        onChange={onChange}
        options={[
          {
            label: t('common:yes'),
            value: true
          },
          {
            label: t('common:no'),
            value: false
          }
        ]}
      />
    </div>
  )
}

export default function ProductionRouteHeat({
  next,
  previous,
  initialValues
}: {
  initialValues: {
    heatFlowSystem: HeatFlowSystems | null
    heatFlowSodaAshPurification: boolean | null
    heatFlowLimestonePurification: boolean | null
  }
  next: (payload: {
    heatFlowSystem: HeatFlowSystems
    heatFlowSodaAshPurification: boolean
    heatFlowLimestonePurification: boolean
  }) => void
  previous: () => void
}) {
  const { t } = useTranslation()

  const [heatFlowSystem, setHeatFlowSystem] = useState<HeatFlowSystems | null>(
    initialValues.heatFlowSystem
  )

  const [heatFlowSodaAshPurification, setHeatFlowSodaAshPurification] = useState<boolean>(
    initialValues.heatFlowSodaAshPurification || false
  )

  const [heatFlowLimestonePurification, setHeatFlowLimestonePurification] = useState<boolean>(
    initialValues.heatFlowLimestonePurification || false
  )

  const onPreviousClick = useCallback(() => {
    previous()
  }, [previous])

  const onHeatFlowSystemChange = useCallback(
    (value: HeatFlowSystems) => {
      setHeatFlowSystem(value)
      setHeatFlowSodaAshPurification(false)
      setHeatFlowLimestonePurification(false)
    },
    [setHeatFlowSystem, setHeatFlowSodaAshPurification, setHeatFlowLimestonePurification]
  )

  const onNextClick = useCallback(() => {
    if (heatFlowSystem === null) {
      return
    }
    if (heatFlowsWhichRequireFlueGasPurificationInput.includes(heatFlowSystem)) {
      return next({
        heatFlowSystem,
        heatFlowSodaAshPurification: heatFlowSodaAshPurification,
        heatFlowLimestonePurification: heatFlowLimestonePurification
      })
    }
    return next({
      heatFlowSystem,
      heatFlowSodaAshPurification: false,
      heatFlowLimestonePurification: false
    })
  }, [heatFlowSystem, next, heatFlowSodaAshPurification, heatFlowLimestonePurification])

  const heatFlowsWhichRequireFlueGasPurificationInput = [
    HeatFlowSystems.Cogeneration,
    HeatFlowSystems.NoCogeneration
  ]

  const isNextButtonEnabled = heatFlowSystem !== null

  const options = Object.values(HeatFlowSystems).map(value => {
    const label = t(`productionRoute:heat.options.${value}.label`)
    const isChecked = heatFlowSystem === value
    const isOpen = heatFlowsWhichRequireFlueGasPurificationInput.includes(value) && isChecked
    return {
      label,
      value,
      id: value,
      isChecked,
      isOpen,
      children: isOpen ? (
        <div className="space-y-4">
          <PurificationOptionForm
            label={t('productionRoute:heat.limestonePurification.label')}
            value={heatFlowLimestonePurification}
            name="limestonePurification"
            onChange={setHeatFlowLimestonePurification}
          />

          <PurificationOptionForm
            label={t('productionRoute:heat.sodaAshPurification.label')}
            value={heatFlowSodaAshPurification}
            name="sodaAshPurification"
            onChange={setHeatFlowSodaAshPurification}
          />
        </div>
      ) : null
    }
  })

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle title={t('productionRoute:heat.title')} />
        </CardHeader>
        <CardBody className="space-y-4">
          <Alert type="info">{t('productionRoute:heat.callout')}</Alert>
          <p className="text-sm">{t('productionRoute:heat.description')}</p>
          <SelectListSingle<HeatFlowSystems>
            className="space-y-2"
            items={options}
            name="heatFlowSystems"
            onChange={value => {
              onHeatFlowSystemChange(value)
            }}
          />
        </CardBody>
      </Card>
      <StepFooter
        onPrevious={onPreviousClick}
        onNext={onNextClick}
        isNextEnabled={isNextButtonEnabled}
      />
    </div>
  )
}
